/**
 * НАЗНАЧЕНИЕ: Главная таблица слайдов с пагинацией и поиском
 * ОПИСАНИЕ: Компонент отображает список всех слайдов пользователя с функциями:
 * - Пагинация для больших списков
 * - Поиск по названию файла (debounce 2 сек)
 * - Автоматическое обновление статуса обработки слайдов
 * - Отслеживание просмотренных слайдов
 * - Множественный выбор слайдов для операций
 * АВТООБНОВЛЕНИЕ: useMultiSlidePolling для DZI конвертации и ИИ-анализа
 * ПРОИЗВОДИТЕЛЬНОСТЬ: Sticky заголовки, фиксированная высота таблицы
 */
import React, {useEffect, useState} from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Container from "@mui/material/Container";
import Pagination from "@mui/material/Pagination";
import Checkbox from "@mui/material/Checkbox";
import Tooltip from "@mui/material/Tooltip";

import {HEADERS} from "../constants";
import {useDispatch, useSelector} from "react-redux";
import {
    fetchSlidesByPage,
    loadViewedSlidesFromStorage,
    fetchSlideById,
    addSelectedSlides,
    removeSelectedSlides,
} from "../../../shared/model/redux/slides-reducer";
import Loader from "./loader";
import {Error} from "@mui/icons-material";
import TextField from "@mui/material/TextField";
import {SlidesTableRow} from "../atoms/SlidesTableRow";
import {useMultiSlidePolling} from "../../../shared/hooks/useMultiSlidePolling.js";
import {getNextOrdering} from "../lib/sorting";

function TableWithPagination() {

    const dispatch = useDispatch();

    const [requestParams, setRequestParams] = useState({
        page: 1,
        search: '',
        ordering: '-id',
    });

    const [searchInput, setSearchInput] = useState('');

    const loading = useSelector((store) => store.slides.loading)
    const isError = useSelector((store) => store.slides.isError)
    const rows = useSelector((store) => store.slides.data.results) //сортируем по убыванию
    const total_pages = useSelector((store) => store.slides.data.total_pages)
    const selected = useSelector((store) => store.slides.selected)

    // Состояние чекбокса в шапке: отмечены все строки страницы, часть или ни одной
    const selectedIds = selected.map((slide) => slide.id);
    const selectedOnPageCount = (rows || []).filter(
        (row) => selectedIds.includes(row.id)).length;
    const allOnPageSelected =
        rows?.length > 0 && selectedOnPageCount === rows.length;
    const someOnPageSelected =
        selectedOnPageCount > 0 && !allOnPageSelected;

    useEffect(() => {
        dispatch(fetchSlidesByPage(requestParams));
    }, [dispatch, requestParams]);

    useEffect(() => {
        // Загружаем просмотренные слайды из localStorage при инициализации компонента
        // Это нужно для синхронизации, если данные изменились в другой вкладке
        dispatch(loadViewedSlidesFromStorage());

        // Добавляем слушатель для синхронизации между вкладками
        const handleStorageChange = (e) => {
            if (e.key === 'viewedSlides') {
                dispatch(loadViewedSlidesFromStorage());
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [dispatch]);

    // Множественный опрос слайдов с разными условиями
    useMultiSlidePolling(rows, dispatch, fetchSlideById, [
        {
            filter: slide => !slide.dzi,
            interval: 15000,
            logPrefix: 'DZI:'
        },
        {
            filter: slide => 
                slide.result === null && 
                slide.id_task !== null && 
                slide.id_task !== undefined &&
                slide.id_task !== '' &&
                slide.id_task.trim() !== '',
            interval: 15000,
            logPrefix: 'Processing:'
        }
    ]);

    // Debounce для поиска - отправляем запрос через 2 секунды после прекращения ввода
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (searchInput !== requestParams.search) {
                setRequestParams(prevParams => ({
                    ...prevParams,
                    page: 1,
                    search: searchInput
                }));
            }
        }, 2000);

        return () => clearTimeout(timeoutId);
    }, [searchInput, requestParams.search]);

    useEffect(() => {
        let handler = (e) => {
            if (e.key === 'Enter' || e.keyCode === 13) {
                // При нажатии Enter сразу отправляем запрос, не ждем 2 секунды
                setRequestParams(prevParams => ({
                    ...prevParams,
                    page: 1,
                    search: searchInput
                }));
            }
        }
        document.addEventListener("keydown", handler)
        return () => {
            document.removeEventListener("keydown", handler)
        }
    }, [searchInput])



    const setSearch = (e) => {
        const { value } = e.target;
        setSearchInput(value); // Обновляем только локальное состояние поля ввода
    };

    const handleChange = (event, value) => {
        setRequestParams({...requestParams, page: value});
    };

    // Отметить или снять отметку сразу со всех строк текущей страницы.
    // Выбранное на других страницах не трогаем — операции меню работают
    // со всем накопленным списком
    const handleSelectAllOnPage = () => {
        if (!rows?.length) return;

        if (allOnPageSelected) {
            dispatch(removeSelectedSlides(rows.map((row) => row.id)));
        } else {
            dispatch(addSelectedSlides(rows));
        }
    };

    const handleSort = (field) => {
        setRequestParams(prevParams => ({
            ...prevParams,
            page: 1,
            ordering: getNextOrdering(prevParams.ordering, field),
        }));
    };

    if (loading) return <Loader/>

    if (isError) return <Error/>

    return (
        <>
            <div style={{width: '500px', margin: '0 20px -25px auto'}}>
                <TextField
                    id="search"
                    label="Введите имя слайда для поиска"
                    variant="standard"
                    sx={{width: "97%", margin: 1}}
                    value={searchInput}
                    onChange={(e) => setSearch(e)}
                />
            </div>
            <div>
                <Container sx={{padding: "24px"}} maxWidth={false}>
                    {/* Контейнер для таблицы с фиксированным заголовком */}
                    <div style={{
                        height: '72vh', // Увеличиваем высоту таблицы
                        overflow: 'auto',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px'
                    }}>
                        <Table
                            sx={{minWidth: 650}}
                            aria-label="simple table"
                            stickyHeader // Включаем sticky заголовок
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            backgroundColor: '#f5f5f5',
                                            fontWeight: 'bold',
                                            borderBottom: '2px solid #ddd'
                                        }}
                                    >
                                        <Tooltip title={allOnPageSelected
                                            ? "Снять выделение со всех на странице"
                                            : "Выделить все на странице"}>
                                            {/* span нужен, чтобы Tooltip работал
                                                с отключенным чекбоксом */}
                                            <span>
                                                <Checkbox
                                                    checked={allOnPageSelected}
                                                    indeterminate={someOnPageSelected}
                                                    disabled={!rows?.length}
                                                    onChange={handleSelectAllOnPage}
                                                    inputProps={{
                                                        "aria-label": "Выделить все слайды на странице",
                                                    }}
                                                />
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                    {HEADERS.map((header) => {
                                        const isActive = requestParams.ordering.replace(/^-/, '') === header.orderingField;
                                        const direction = requestParams.ordering.startsWith('-') ? 'desc' : 'asc';

                                        return (
                                        <TableCell
                                            key={header.label}
                                            align="center"
                                            sx={{
                                                backgroundColor: '#f5f5f5',
                                                fontWeight: 'bold',
                                                borderBottom: '2px solid #ddd'
                                            }}
                                        >
                                            {header.orderingField ? (
                                                <TableSortLabel
                                                    active={isActive}
                                                    direction={isActive ? direction : 'asc'}
                                                    onClick={() => handleSort(header.orderingField)}
                                                >
                                                    <span style={{fontWeight: 'bold'}}>{header.label}</span>
                                                </TableSortLabel>
                                            ) : (
                                                <span style={{fontWeight: 'bold'}}>{header.label}</span>
                                            )}
                                        </TableCell>
                                        );
                                    })}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {rows?.length > 0 ? (
                                    rows.map((row, index) => (
                                        <SlidesTableRow 
                                            row={row} 
                                            key={index} 
                                            cls={index % 2 ? '' : 'colored'}
                                        />
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={HEADERS.length + 1} align="center">
                                            Нет данных
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {/* Пагинация вынесена за пределы скроллируемой области */}
                    <Pagination
                        className={'pagination'}
                        sx={{margin: "14px 0", display: 'flex', justifyContent: 'center'}}
                        count={total_pages}
                        page={requestParams.page}
                        onChange={handleChange}
                    />
                </Container>
            </div>
        </>
    );
}

export default TableWithPagination;
